import React, { useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { ref, get } from "firebase/database";
import { auth, database as realtimeDb } from "../FirebaseConfig";
import { useLocation, useNavigate } from "react-router-dom";
import Lottie from "lottie-react";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";

export default function OrdersPage() {
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [animationData, setAnimationData] = useState(null);
  const [expandedOrders, setExpandedOrders] = useState({});
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch("/assets/General/orderLoading.json")
      .then((response) => response.json())
      .then((data) => setAnimationData(data))
      .catch((error) => console.error("Error loading animation:", error));
  }, []);

  useEffect(() => {
    const redirectPath =
      new URLSearchParams(location.search).get("redirect") || "/orders";

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        fetchUserOrders(currentUser.uid);
      } else {
        setUser(null);
        setOrders([]);
        navigate(`/login?redirect=${redirectPath}`);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const fetchUserOrders = async (uid) => {
    setLoading(true);
    try {
      const ordersRef = ref(realtimeDb, "orders");
      const snapshot = await get(ordersRef);

      if (snapshot.exists()) {
        const allOrders = snapshot.val();
        const userOrders = Object.entries(allOrders)
          .filter(([_, order]) => order.userId === uid && order.paymentStatus !== "Failed") // Exclude failed payments
          .map(([orderId, order]) => ({ orderId, ...order }))
          .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

        setOrders(userOrders);
      } else {
        setOrders([]);
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
    }
    setLoading(false);
  };

  const toggleExpand = (orderId) => {
    setExpandedOrders((prev) => ({
      ...prev,
      [orderId]: !prev[orderId],
    }));
  };

  const checkPaymentStatus = (orderId,mId) => {
    navigate(`/payment-stat?id=${orderId}&mId=${mId}`);
  };

  const getTimeDifference = (createdAt) => {
    const currentTime = new Date().getTime();
    return (currentTime - createdAt) / (1000 * 60); // Convert to minutes
  };

  if (loading) {
    return (
      <div className="h-screen flex justify-center items-center">
        <div className="w-full md:w-1/2">
          <Lottie animationData={animationData} loop autoPlay />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto mt-27 p-6">
      <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 text-center">
        My Orders
      </h1>

      {orders.length > 0 ? (
        <div className="mt-4 space-y-4">
          {orders.map((order) => {
            const timeDiff = getTimeDifference(order.createdAt);
            return (
              <div
                key={order.orderId}
                className="bg-white p-4 md:p-5 rounded-lg shadow-md border border-gray-200"
              >
                <h2 className="text-sm md:text-base lg:text-2xl font-semibold text-gray-800">
                  Order ID: {order.orderId}
                </h2>
                <p className="text-sm md:text-base text-gray-600">
                  <span className="font-medium">Total Amount:</span> ₹{order.totalAmount}
                </p>
                <p className="text-sm md:text-base text-gray-600">
                  <span className="font-medium">Order Status:</span> {order.orderStatus}
                </p>

                {/* Handle Payment Status */}
                {order.paymentStatus === "Pending" ? (
                  <div className="mt-4 p-4 bg-yellow-100 border-l-4 border-yellow-500 rounded-lg shadow-md">
                    <p className="text-sm md:text-lg lg:text-base font-semibold text-yellow-700">
                      ⏳ We are processing your payment... Please be patient. 🐝🍯
                    </p>
                    {timeDiff > 25 && (
                      <button
                        onClick={() => checkPaymentStatus(order.orderId,order.paymentId)}
                        className="mt-2 px-4 py-2 text-white text-sm md:text-base font-semibold 
                        bg-[#f47d02] hover:bg-black transition-all duration-300 
                        rounded-lg shadow-md"
                      >
                        Check Payment Status
                      </button>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={() => navigate(`/order?id=${order.orderId}`)}
                    className="mt-6 w-full md:w-auto px-6 py-3 text-white text-sm md:text-base font-semibold 
                    bg-[#f47d02] hover:bg-black transition-all duration-300 
                    rounded-lg shadow-md flex items-center justify-center space-x-2"
                  >
                    🚚 Track Order
                  </button>
                )}

                <div className="mt-4">
                  <div
                    className="flex justify-between items-center cursor-pointer bg-gray-100 p-3 rounded-lg"
                    onClick={() => toggleExpand(order.orderId)}
                  >
                    <h3 className="text-md md:text-lg font-semibold text-gray-700">
                      Products ({order.products.length})
                    </h3>
                    {expandedOrders[order.orderId] ? (
                      <FaChevronUp className="text-gray-600" />
                    ) : (
                      <FaChevronDown className="text-gray-600" />
                    )}
                  </div>

                  {expandedOrders[order.orderId] && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-2">
                      {order.products.map((product, index) => {
                        const discount = product.price - product.actualPrice; // Calculate discount
                        return (
                          <div
                            key={index}
                            className="flex items-center space-x-4 p-3 bg-gray-50 rounded-lg border shadow-sm"
                          >
                            <img
                              src={product.thumbnail}
                              alt={product.name}
                              className="h-16 w-16 rounded-lg shadow-md"
                            />
                            <div>
                              <p className="text-sm md:text-base font-medium">{product.name}</p>
                              <p className="text-xs md:text-sm text-gray-500">
                                Quantity: {product.quantity}
                              </p>
                              <div className="flex items-center space-x-2">
                                <p className="text-xs md:text-sm text-gray-500">
                                  Price: ₹{product.actualPrice}
                                </p>
                                {discount > 0 && (
                                  <p className="text-xs md:text-sm text-gray-400 line-through">
                                    ₹{product.price}
                                  </p>
                                )}
                              </div>
                              {discount > 0 && (
                                <p className="text-xs md:text-sm text-green-600 font-semibold">
                                  You save ₹{discount * product.quantity}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-gray-600 mt-6 text-center text-lg">No orders found.</p>
      )}
    </div>
  );
}
